import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CpMarketplaceApiService } from '../../../services/cp-marketplace-api.service';
import { SharedDataService } from '../../../services/shared-data.service';
import { ISSUER_MESSAGES } from 'src/app/messages';
import { AllDocsAdditionalData, DocumentKeys, FileTypes } from 'appRootPath/models/commercial-papers.model';
import { API_DATE_FORMAT, FileObject, MODAL_OPTIONS } from 'appRootPath/shared/global-meta-data';
import { ViewDocsModalComponent } from 'appRootPath/shared/view-docs-modal/view-docs-modal.component';
import { getCalendarDateObj, getDateFromCalendarObj } from 'appRootPath/utils';
import * as moment from 'moment';

@Component({
  selector: 'app-promissory-note',
  templateUrl: './promissory-note.component.html',
  styleUrls: ['./promissory-note.component.scss']
})

export class PromissoryNoteComponent implements OnInit {
  @Input() docKey: DocumentKeys;
  @Input() issueId: string;
  @Input() prefillData: PromissoryNoteAdditionalData;

  existingStampPaperDoc: FileObject;
  promissoryDataForm: FormGroup;
  isFormInvalid = false;
  fileTypes: FileTypes[] = ['.pdf'];

  constructor(
    private promissoryFB: FormBuilder,
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
    private apiService: CpMarketplaceApiService,
    private sharedData: SharedDataService,
    private modalService: NgbModal
  ) { }

  /**
   * OnInit Lifecycle Hook.
   * Initializes promissory note form data and stamp paper with prefilled data if any.
   */
  ngOnInit() {
    this.initPromissoryNoteForm(this.prefillData);

    if (this.prefillData) {
      this.setExisitingStampPaperDocValue(this.prefillData.stamp_paper_doc);
    }
  }

  /**
   * Initialises the Promissory Note Form.
   */
  initPromissoryNoteForm(prefillData: PromissoryNoteAdditionalData) {
    this.promissoryDataForm = this.promissoryFB.group({
      serial_number: (prefillData && prefillData.serial_number) || '',
      place: (prefillData && prefillData.place) ||
        this.sharedData.getUserDataFromLS().user_company.place_of_issue,
      stamp_paper_doc:
        [(prefillData && prefillData.stamp_paper_doc.file_key) || null, Validators.required]
    });
  }

  /**
   * Sets preuploaded file to show on the form.
   */
  setExisitingStampPaperDocValue(doc: FileObject) {
    this.existingStampPaperDoc = doc;
  }

  /**
   * Uploads raw file and gets the file token from the BE.
   * @param fileContent the raw file as JS Object.
   */
  setStampPaperDoc(fileContent) {
    if (fileContent) {
      const formDataRequest = new FormData();
      formDataRequest.append('files', fileContent);

      this.apiService.uploadFileAndGetToken(formDataRequest).subscribe((fileTokenResp: string[]) => {
        if (fileTokenResp) {
          this.toastr.success(ISSUER_MESSAGES.PROOF_OF_STAMP_DUTY_UPDATED);
          // uploadFileAndGetToken api will always return an array of files.
          // Since we are sending only one document, we are reading only the first document from resp.
          this.promissoryDataForm.controls.stamp_paper_doc.setValue(fileTokenResp[0]);
          this.existingStampPaperDoc = null;
        }
      });
    }
  }

  /**
   * Post the additional data for the current issue ID and document to the server
   */
  postPromissoryNoteData(promissoryFormData) {
    promissoryFormData.document_date =
      getDateFromCalendarObj(promissoryFormData.document_date).format(API_DATE_FORMAT);

    if (this.promissoryDataForm.invalid) {
      this.toastr.warning(ISSUER_MESSAGES.NO_PROOF_OF_STAMP_DUTY_SUBMIT);
      this.isFormInvalid = true;
    } else {
      this.isFormInvalid = false;

      const promissoryNoteAdditionalData = {
        issue: this.issueId,
        promissory_note: Object.assign({}, promissoryFormData)
      };

      this.apiService.postAdditionalData(promissoryNoteAdditionalData).
        subscribe((res: AllDocsAdditionalData) => {
          if (res.id) {
            this.toastr.success(ISSUER_MESSAGES.PROMISSORY_ADDN_DATA_SUCCESS);
            this.activeModal.close(res.promissory_note);
          }
        });
    }
  }

  /**
   * Makes an API call to get the file url and shows the file in the view docs modal.
   * @param docUrl - URL of the doc to load.
   */
  showDoc(docUrl) {
    this.apiService.fetchFile(docUrl).subscribe(fileResp => {
      const modalRef = this.modalService.open(ViewDocsModalComponent, MODAL_OPTIONS);
      modalRef.componentInstance.docsToView = [{
        title: `Proof of Stamp Duty Payment`,
        document_url: fileResp
      }];
    });
  }
}

interface PromissoryNoteAdditionalData {
  place: string;
  serial_number: string;
  document_date: string;
  stamp_paper_doc: FileObject;
}